import React from 'react';
import { UploadIcon } from "../Icons";
import { Button } from "../ui/button";

/**
 * Props interface for the FileUploadArea component
 * @property onDrop - Optional handler for file drop events
 * @property onBrowse - Handler for file selection via input
 * @property fileInputId - Unique ID for the file input element
 * @property accept - Accepted file types (e.g., "image/png,image/jpeg")
 * @property previewContent - Optional ReactNode to display file preview
 * @property uploadText - Optional custom text for upload area
 * @property clearPreview - Function to clear the current preview
 * @property previewUrls - Array of URLs for multiple preview images
 * @property previewType - Type of preview ('image' or 'model')
 * @property previewName - Name of the preview model
 */
interface FileUploadAreaProps {
  onDrop?: (e: React.DragEvent) => void;
  onBrowse: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputId: string;
  accept: string;
  previewContent?: React.ReactNode;
  uploadText?: string;
  clearPreview: () => void;
  previewUrls?: string[];
  previewType: 'image' | 'model';
  previewName?: string | null;
}

/**
 * Reusable file upload component with drag-and-drop and preview functionality
 * @param props - FileUploadAreaProps
 * @returns JSX.Element - Rendered file upload area
 */
export const FileUploadArea: React.FC<FileUploadAreaProps> = ({ 
  onDrop = (e) => e.preventDefault(),
  onBrowse, 
  fileInputId, 
  accept, 
  uploadText = "Drop your file here",
  previewType,
  previewUrls,
  previewName,
  clearPreview
}) => {

  const renderPreview = () => {
    if (previewType === 'image') {
      if (!previewUrls?.length) return null;
      return (
        <div className="relative">
          <div className="flex flex-wrap gap-4 justify-center">
            {previewUrls.map((url, index) => (
              <img
                key={index}
                src={url}
                alt={`Preview ${index + 1}`}
                style={{
                  maxWidth: "100px",
                  maxHeight: "100px",
                  objectFit: "contain",
                }}
              />
            ))}
          </div>
        </div>
      );
    } else {
      if (!previewName) return null;
      return (
        <div className="w-[100px] h-[100px] bg-secondary-background rounded-lg flex items-center justify-center border-2 border-border neo-shadow">
          <div className="text-foreground/80 text-sm text-center">Model uploaded</div>
        </div>
      );
    }
  };

  const openFileDialog = () => document.getElementById(fileInputId)?.click();

  return (
    // Stays a div (it nests real buttons), but is fully keyboard-operable:
    // Enter/Space open the same file dialog the click does (WCAG 2.5.7 —
    // dragging must never be the only path).
    <div
      role="button"
      tabIndex={0}
      aria-label={`${uploadText}. Press Enter to browse files.`}
      className="border-2 border-dashed border-border rounded-lg p-8 mb-4 cursor-pointer relative bg-secondary-background neo-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
      onClick={openFileDialog}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openFileDialog();
        }
      }}
    >
      {(previewType === 'image' ? (previewUrls ?? []).length > 0 : Boolean(previewName)) ? (
        <div className="flex flex-col items-center">
          <div className="absolute top-2 right-2">
            <button
              type="button"
              aria-label="Remove uploaded files"
              className="text-foreground bg-secondary-background border-2 border-border hover:translate-x-[2px] hover:translate-y-[2px] w-6 h-6 rounded-full neo-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
              onClick={(e) => {
                e.stopPropagation();
                clearPreview();
              }}
            >
              ✕
            </button>
          </div>
          {renderPreview()}
        </div>
      ) : (
        <div className="flex justify-center flex-col items-center">
          <div className="rounded-full p-4 align-middle justify-self-center bg-main/20 mb-6">
            <UploadIcon />
          </div>
          <p className="text-foreground text-lg font-semibold">
            {uploadText}
          </p>
          <p className="text-foreground/80 text-lg font-medium mb-6">
            or click to browse
          </p>
          <Button className="px-8 py-2" type="button">Browse</Button>
        </div>
      )}
      <input
        type="file"
        id={fileInputId}
        className="hidden"
        accept={accept}
        onChange={onBrowse}
        multiple={previewType === 'image'}
      />
    </div>
  );
}; 


/**
 * Props interface for the ModelDownloadSection component
 * @property result - URL of the 3D model to download
 * @property selectedFormat - Currently selected file format (glb/fbx/obj)
 * @property setSelectedFormat - Function to update the selected format
 * @property handleDownload - Function to handle the download process
 * @property isDownloading - Boolean indicating if download is in progress
 */
interface ModelDownloadSectionProps {
  result: string | null;
  selectedFormat: string;
  setSelectedFormat: (format: string) => void;
  handleDownload: () => Promise<void>;
  isDownloading: boolean;
}

/**
 * Provides UI for selecting model format and initiating downloads
 * @param props - ModelDownloadSectionProps
 * @returns JSX.Element | null - Returns null if no model result is available
 */
export const ModelDownloadSection: React.FC<ModelDownloadSectionProps> = ({
  result,
  selectedFormat,
  setSelectedFormat,
  handleDownload,
  isDownloading
}) => {
  if (!result) return null;
  return (
    <>
      {/* Format Selection Buttons */}
      <div className="flex justify-center mt-4 mb-2">
        <div className="flex border-2 border-border rounded-md overflow-hidden neo-shadow">
          {["glb", "fbx", "obj"].map((format, index) => (
            <button
              key={format}
              onClick={() => setSelectedFormat(format)}
              className={`px-10 py-1.5 font-semibold transition-all ${
                selectedFormat === format
                  ? "bg-main text-[color:var(--color-main-foreground)]"
                  : "bg-secondary-background text-foreground hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none"
              } ${
                index !== 2 ? "border-r-2 border-border" : ""
              }`}
            >
              {format.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Download Button */}
      <Button className="w-full mt-2" onClick={handleDownload} disabled={isDownloading}>
        {isDownloading ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-main border-t-transparent rounded-full animate-spin" />
            <span>Downloading...</span>
          </div>
        ) : (
          "Download Model"
        )}
      </Button>
    </>
  );
}; 
